import { useState } from 'react'
import type { FieldDefinition } from '@/api/types'

/** Persisted settings for the Contacts table view (localStorage 'pp.contactsTable'). */

export const CONTACTS_TABLE_STORAGE_KEY = 'pp.contactsTable'

export const PAGE_SIZES = [25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]

/** Prefix for contact custom-field column ids, e.g. 'custom.budget'. */
export const CUSTOM_COLUMN_PREFIX = 'custom.'

/** A column id is a built-in key ('name', 'email', …) or a 'custom.<fieldKey>'. */
export type ColumnId = string

/** Sort keys the backend understands (GET /api/contacts/page?sort=). */
export type ContactSortKey =
  | 'name'
  | 'email'
  | 'phone'
  | 'owner'
  | 'leadSource'
  | 'createdAt'
  | 'lastContactedAt'
  | 'nextContactDate'

export interface ColumnDef {
  id: ColumnId
  label: string
  /** Server sort key; absent for custom columns (the server can't sort by them). */
  sortKey?: ContactSortKey
  /** Locked columns (name) are always visible and can't be removed. */
  locked?: boolean
}

/** Built-in column pool in canonical order. Custom columns are appended per agency at runtime. */
export const COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Contact', sortKey: 'name', locked: true },
  { id: 'mobile', label: 'Mobile' },
  { id: 'email', label: 'Email', sortKey: 'email' },
  { id: 'phone', label: 'Phone', sortKey: 'phone' },
  { id: 'owner', label: 'Owner', sortKey: 'owner' },
  { id: 'source', label: 'Source', sortKey: 'leadSource' },
  { id: 'lastActivity', label: 'Last activity', sortKey: 'lastContactedAt' },
  { id: 'nextContact', label: 'Next contact', sortKey: 'nextContactDate' },
  { id: 'openDeals', label: 'Open deals' },
  { id: 'created', label: 'Created', sortKey: 'createdAt' },
]

/** Contact custom fields as column defs ('custom.<key>' ids); no server sort key. */
export function customContactColumns(defs: FieldDefinition[] | undefined): ColumnDef[] {
  return (defs ?? []).map((d) => ({ id: CUSTOM_COLUMN_PREFIX + d.fieldKey, label: d.label }))
}

export interface TableSettings {
  pageSize: PageSize
  /** Visible column ids in canonical order; always contains 'name'. */
  columns: ColumnId[]
}

export const DEFAULT_TABLE_SETTINGS: TableSettings = {
  pageSize: 25,
  // CRM-focused default (mockup): contact, mobile, email, owner, source, last activity, next contact, open deals.
  columns: ['name', 'mobile', 'email', 'owner', 'source', 'lastActivity', 'nextContact', 'openDeals'],
}

/** Coerces parsed JSON of unknown shape to valid settings, falling back per field. */
function normalize(raw: unknown): TableSettings {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const pageSize = (PAGE_SIZES as readonly number[]).includes(obj.pageSize as number)
    ? (obj.pageSize as PageSize)
    : DEFAULT_TABLE_SETTINGS.pageSize
  const requested = Array.isArray(obj.columns)
    ? (obj.columns as unknown[])
    : DEFAULT_TABLE_SETTINGS.columns
  // Preserve the user's column ORDER; keep built-in ids or 'custom.<key>' (unknown custom columns
  // are dropped at render time). 'name' is locked — always present and first.
  const builtinIds = new Set(COLUMNS.map((c) => c.id))
  const kept: string[] = []
  for (const id of requested) {
    if (typeof id !== 'string' || kept.includes(id)) continue
    if (!id.startsWith(CUSTOM_COLUMN_PREFIX) && !builtinIds.has(id)) continue
    kept.push(id)
  }
  const columns = ['name', ...kept.filter((c) => c !== 'name')]
  return { pageSize, columns }
}

export function loadTableSettings(): TableSettings {
  try {
    const raw = localStorage.getItem(CONTACTS_TABLE_STORAGE_KEY)
    if (!raw) return DEFAULT_TABLE_SETTINGS
    return normalize(JSON.parse(raw))
  } catch {
    return DEFAULT_TABLE_SETTINGS
  }
}

export function saveTableSettings(settings: TableSettings) {
  localStorage.setItem(CONTACTS_TABLE_STORAGE_KEY, JSON.stringify(settings))
}

/** Contacts table-view settings backed by localStorage: [settings, update]. */
export function useContactsTableSettings(): [TableSettings, (next: TableSettings) => void] {
  const [settings, setSettings] = useState<TableSettings>(loadTableSettings)
  function update(next: TableSettings) {
    setSettings(next)
    saveTableSettings(next)
  }
  return [settings, update]
}
